import React, { Fragment, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeTags } from 'Core/hooks';
import { useIntl } from 'react-intl';
import CryptoJS from 'crypto-js';
import moment from 'moment';
// Helpers
import { GetFormatForDate } from 'Core/data/Helpers';
import { formatExpirationDateForPayload, parseExpirationDateForInput } from './dateUtils';
// Actions
import {
  resetCognitoSubUser,
  createSubUser,
  updateSubUser,
  searchSubUserRoles,
  clearRedux
} from "Redux/actions";
import { createSubUserAvatar } from '../../redux/actions';
import { getSecureFile } from 'Redux/actions';
// Core
import { Grid, IconButton, Icon, Box } from '@mui/material';
// Components
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Button, Form, Input, InputDate, Select, Tooltip } from 'Components';
import InputAvatar from 'Components/InputAvatar';
import CreditEditRol from 'Modules/account/views/role/components/creditEditRole'
import { ToggleSwitch } from 'Components';
import { useSupervisorPermission } from '../../../../../.common/utils/supervisorPermission';
import TagsManage from 'Modules/.common/components/TagsManage';
import { HasPermission } from 'Core/security';

const CreditEditSubUser = (props) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  //Props
  const { onShow, onClose, subUserInfo } = props;

  //Ref
  const formRef = useRef();
  const pendingFormValuesRef = useRef({});

  //Local state
  const [formData, setFormData] = useState({});
  const [onShowPermissions, setOnShowPermissions] = useState(false);
  const [showSliderPrincipal, setShowSliderPrincipal] = useState(false);
  const [onShowModalResetUser, setOnShowModalResetUser] = useState(false);

  const [errorMessage, setErrorMessage] = useState();
  const [eldCredentials, setEldCredentials] = useState(false);

  //Redux
  const { user } = useSelector((state) => state.securityRedux);
  const { subUserSelected, loadingSubUser, loadingSubUserRoles, subUserRoles, uploadingAvatar, uploadedAvatar, avatarUploadError } = useSelector(state => state.subUserRedux);

  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
  const { allfilters: filtersRole } = useSelector((state) => state.roleRedux);
  const { loadingCurrentSubUser, currentSubUser } = useSelector((state) => state.subUserRedux);
  const secureUrl = useSelector((state) => state.commomRedux?.updateSubUser);
  const apolloEldPermission = HasPermission(user, 'apolloeld')
  const selectedSubUserContext = subUserSelected?.id ? subUserSelected : subUserInfo;

  const resetUserCognito = useCallback((id, email) => {
    dispatch(resetCognitoSubUser({ id, email }));
  });

  const consolidateIdModulesChecked = (modules) => {
    let result = [];
    modules?.forEach((module) => {
      result.push(module?.id)
      module?.subModules?.forEach((subModule) => {
        result.push(subModule.id)
      })
    })
    return result;
  }

  const validateUploadAvatar = useCallback((params, formValues = {}) => {
    // Inputs debounce onChange (300ms), so formData can lag behind what the user typed;
    // Form passes the live values collected from the inputs.
    const { firstName, lastName, userName, email } = formValues;
    pendingFormValuesRef.current = _.pickBy(
      { firstName, lastName, userName, email },
      (value) => value !== undefined
    );

    if (formData?.inputAvatar) {
      dispatch(
        createSubUserAvatar({
          ...formData.inputAvatar,
          fileKeyDelete: formData?.avatarFileId || subUserSelected?.avatarFileId || null,
        })
      );
      return;
    }
    consolidateData(params, pendingFormValuesRef.current);
  }, [formData, subUserSelected, dispatch]);

  const consolidateData = (params, formValues = {}, uploadedAvatarFileId = undefined) => {
    if (!formData.tags?.length) {
      setErrorMessage(messages['unit_settings_tags_required']);
      return;
    }
    // Merge formData with current input values (formValues has values directly from inputs,
    // which may be more up-to-date than formData due to debouncing)
    const mergedData = { ...formData, ...formValues };
    const {
      id,
      firstName,
      lastName,
      role,
      customRole,
      userName,
      tags,
      expiration,
      phone,
      password,
      email,
      eldUser,
    } = mergedData;

    let currentEldPassword = subUserSelected?.eldUser?.password ? CryptoJS.AES.decrypt(subUserSelected?.eldUser?.password, 'gpstrackit') : '';
    let originalPassword = currentEldPassword?.toString(CryptoJS.enc.Utf8) || subUserSelected?.eldUser?.password || '';
    let newPassword = formData?.eldUser?.password || originalPassword ? CryptoJS.AES.encrypt(formData?.eldUser?.password || originalPassword, 'gpstrackit').toString() : ''

    const finalAvatarFileId =
      uploadedAvatarFileId !== undefined
        ? uploadedAvatarFileId
        : (formData?.avatarFileId ?? null);

    let result = {
      id,
      firstName,
      lastName,
      userName,
      expiryDate: (expiration ? formatExpirationDateForPayload(expiration) : null),
      tagIds: tags.map((tag) => tag.id),
      roleId: (role ? role.id : customRole.id) || 0,
      phone,
      eldUser: {
        ...eldUser,
        password: newPassword,
      },
      email,
      avatarFileId: finalAvatarFileId
    }

    // Only the password when the user is created
    if (!id) {
      result = {
        ...result,
        password,
        email
      }
    }

    if (customRole?.id === 0 || customRole?.isCustom) {
      result = {
        ...result,
        customRole: true,
        isSupervisor: customRole?.isSupervisor,
        roleModules: consolidateIdModulesChecked(customRole?.modules || [])
      }
    }

    if (eldCredentials) {
      delete result?.eldUser["confirmPassword"]
      delete result?.eldUser["__typename"]
    } else {
      result = {
        ...result,
        eldUser: {
          username: '',
          password: '',
        }
      }
    }

    // Preserve avatarFileId even if it is null (to delete it from the server)
    const preserveAvatarFileId = result.avatarFileId;
    let sanitazedResult = _.pickBy(result, (value) => !(value === undefined || value == null));
    
    // Re-add avatarFileId if it was removed by pickBy but was explicitly set to null
    if (preserveAvatarFileId === null && result.hasOwnProperty('avatarFileId')) {
      sanitazedResult.avatarFileId = null;
    }

    if (sanitazedResult?.id) {
      dispatch(updateSubUser(sanitazedResult));
    } else {
      dispatch(createSubUser(sanitazedResult));
    }
  }

  const turnSlidersOffOn = () => {
    setShowSliderPrincipal(!showSliderPrincipal);
    setOnShowPermissions(!onShowPermissions);
  }

  // Function to process the selected subUser data
  const processSubUserData = useCallback((subUser) => {
    // Clear previous secureUrl before processing
    dispatch(clearRedux("COMMON", { updateSubUser: null }));
    
    // Clear error message when loading user data
    setErrorMessage(undefined);
    
    let result = {
      ...subUser,
      cloneEmail: subUser.email
    }

    result = {
      ...result,
      expiration: parseExpirationDateForInput(result?.expiration)
    }

    if (result?.tagIds) {
      let tagIds = result?.tagIds?.split(",");
      let userTags = []
      if (tagIds?.includes("-99") || tagIds?.includes(-99)) {
        userTags = [{ id: -99 }];
      } else {
        userTags = tags?.filter(tag => tagIds?.includes(`${tag?.id}`));
      }

      result = {
        ...result,
        tags: userTags
      }
    }
    if (result?.eldUser?.username) {
      result = {
        ...result,
        eldUser: {
          ...result.eldUser,
          password: ''
        }
      }
      setEldCredentials(true);
    }

    if (result?.avatarFile?.id) {
      result = {
        ...result,
        avatarFileId: result.avatarFile.id
      }
      // Only request secure URL if there is an avatar
      dispatch(getSecureFile({ fileKey: result.avatarFile.fileKey, timeout: 600, fixedKey: 'updateSubUser' }));
    } else {
      // If there is NO avatar, ensure that formData.avatar is empty
      result = {
        ...result,
        avatar: {},
        avatarFileId: null
      }
    }

    setFormData(result);
  }, [tags, dispatch]);

  const handleClose = useCallback(() => {
    // Clear local states
    setFormData({});
    setErrorMessage(undefined);
    setEldCredentials(false);
    
    // Clear Redux state related to avatar and selected subUser
    dispatch(clearRedux("SUB_USER", { 
      uploadingAvatar: false, 
      uploadedAvatar: null, 
      avatarUploadError: null,
      subUserSelected: null 
    }));
    
    // Clear avatar secureUrl
    dispatch(clearRedux("COMMON", { updateSubUser: null }));
    
    // Call parent's onClose
    onClose();
  }, [dispatch, onClose]);

  useEffect(() => {
    dispatch(searchSubUserRoles({
      ...filtersRole,
      limit: 0
    }));
  }, []);

  useEffect(() => {
    setShowSliderPrincipal(onShow);
    
    if (!onShow) {
      // Clear local states when modal is closed
      setFormData({});
      setErrorMessage(undefined);
      setEldCredentials(false);
      
      // Clear Redux state related to avatar and selected subUser
      dispatch(clearRedux("SUB_USER", { 
        uploadingAvatar: false, 
        uploadedAvatar: null, 
        avatarUploadError: null,
        subUserSelected: null 
      }));
      
      // Clear avatar secureUrl
      dispatch(clearRedux("COMMON", { updateSubUser: null }));
    }
  }, [onShow, dispatch]);

  useEffect(() => {
    if (uploadedAvatar?.fileKey) {
      consolidateData({}, pendingFormValuesRef.current, uploadedAvatar?.id);
    }
  }, [uploadedAvatar]);

  useEffect(() => {
    if (secureUrl && formData?.avatarFileId) {
      setFormData({
        ...formData,
        avatar: { url: secureUrl }
      });
    }
  }, [secureUrl]);

  useEffect(() => {
    if (avatarUploadError) {
      setErrorMessage(messages['avatar_upload_error'] || 'Failed to upload avatar. Please try again.');
    }
  }, [avatarUploadError, messages]);

  useEffect(() => {
    if (onShow && subUserSelected?.id && tags && tags.length > 0) {
      processSubUserData(subUserSelected);
    }
  }, [onShow, subUserSelected, tags, processSubUserData])

    const filterRoles = useMemo(() => {
    if (loadingSubUserRoles) return [];

    const waitingSelectedSubUser = onShow && !!subUserInfo && loadingSubUser && !selectedSubUserContext?.id;
    if (waitingSelectedSubUser) {
      return [];
    }

    const subUserId = user?.isSubUser ? user?.subUserId : '';
    const subUserIdSelected = selectedSubUserContext?.id;
    const didSelectHimself = !!subUserId && subUserId === subUserIdSelected;
    const shouldRestrictWhileLoadingCurrentUser = user?.isSubUser && (loadingCurrentSubUser || !currentSubUser);

    const expiration = selectedSubUserContext?.expiration;
    const hasValidExpiration = expiration && expiration !== '0000-00-00';
    const expiredUser = hasValidExpiration ? moment(expiration).isBefore(moment()) : false;

    if (shouldRestrictWhileLoadingCurrentUser) {
      return subUserRoles.filter((rol) => rol?.isSupervisor === false);
    }

    const validatePermission = useSupervisorPermission(user, currentSubUser);

    if (didSelectHimself || expiredUser || !validatePermission) {
      return subUserRoles.filter((rol) => rol?.isSupervisor === false);
    }

    return subUserRoles;
  }, [
    onShow,
    subUserInfo,
    loadingSubUser,
    loadingSubUserRoles,
    loadingCurrentSubUser,
    user,
    selectedSubUserContext,
    currentSubUser,
    subUserRoles,
  ]);

  return (
    <Fragment>
      {/* Create - Update SubUser */}
      <Modal
        title={`${formData?.id ? `${messages['subUser_edit']} ${formData?.userName || ""}` : messages['subUser_create']}`}
        open={showSliderPrincipal}
        size="small"
        handleClose={handleClose}
        position="right"
        loading={loadingSubUser || loadingCurrentSubUser || uploadingAvatar}
        style={{ overflow: 'hidden' }}
        className="overflow-hidden"
      >
        <ModalBody
          style={{
            overflow: 'hidden',
            marginBottom: 0
          }}
        >
          <div
            style={{
              padding: "0px 24px 24px 24px",
              height: "calc(100vh - 202px)",
              overflow: "auto"
            }}
          >
            <Form
              ref={formRef}
              onValidSubmit={validateUploadAvatar}
            >
              <Grid
                container
                justifyContent={'center'}
                direction={'column'}
                spacing={2}
              >
                <Grid
                  container
                  item
                  spacing={1}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Grid item sm={12}>
                    <InputAvatar
                      label={messages['subUser_avatar'] || 'Avatar'}
                      value={formData?.avatar}
                      name={`${formData?.firstName || ''} ${formData?.lastName || ''}`.trim() || 'User'}
                      accept=".png,.jpg,.jpeg"
                      maxSizeInMB={2}
                      onChange={(value) => {
                        setFormData({
                          ...formData,
                          inputAvatar: value
                        });
                      }}
                      deleteAvatar={() => {
                        setFormData({
                          ...formData,
                          avatar: {},
                          inputAvatar: undefined,
                          avatarFileId: null
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Input
                      id="firstName"
                      type="text"
                      label={messages['subUser_firstName']}
                      placeholder={messages['subUser_firstName']}
                      value={formData?.firstName || ""}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          firstName: value
                        })
                      }
                      minLength={2}
                      minLengthMessage={`${messages['subUser_minMessageUsername']} 2`}
                      maxLength={50}
                      maxLengthMessage={`${messages['subUser_maxMessageUsername']} 50`}
                      required
                      requiredMessage={messages['required_field']}
                    />
                  </Grid>
                  <Grid item xs>
                    <Input
                      id="lastName"
                      type="text"
                      label={messages['subUser_lastName']}
                      placeholder={messages['subUser_lastName']}
                      value={formData?.lastName || ""}
                      onChange={(value) =>
                        setFormData({
                          ...formData,
                          lastName: value
                        })
                      }
                      minLength={2}
                      minLengthMessage={`${messages['subUser_minMessageUsername']} 2`}
                      maxLength={50}
                      maxLengthMessage={`${messages['subUser_maxMessageUsername']} 50`}
                      required
                      requiredMessage={messages['required_field']}
                    />
                  </Grid>
                </Grid>
                {!formData.id &&
                  <>
                    <Grid item sm={12}>
                      <Input
                        id="userName"
                        type="text"
                        label={messages['subUser_userName']}
                        placeholder={messages['subUser_userName']}
                        value={formData?.userName || ""}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            userName: value
                          })
                        }
                        regex={/^[A-Za-z0-9_]*$/}
                        handleKeyDown={32}
                        minLength={2}
                        minLengthMessage={`${messages['subUser_minMessageUsername']} 2`}
                        maxLength={50}
                        maxLengthMessage={`${messages['subUser_maxMessageUsername']} 50`}
                        required
                        requiredMessage={messages['required_field']}
                        disabled={formData?.id > 0}
                        autoComplete="new-password"
                      />
                    </Grid>
                    <Grid
                      container
                      item
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="flex-end"
                    >
                      <Grid item xs={formData?.id ? 10 : 12} sm={formData?.id ? 10 : 12} style={{ paddingRight: '6px' }}>
                        <Input
                          id="email"
                          type="email"
                          label={messages['email']}
                          placeholder={messages['email']}
                          value={formData?.email || ""}
                          onChange={(value) =>
                            setFormData({
                              ...formData,
                              email: value
                            })
                          }
                          email
                          emailMessage={messages['subUser_emailMessage']}
                          minLength={6}
                          minLengthMessage={`${messages['subUser_minLengthMessageEmail']} 6`}
                          maxLength={100}
                          maxLengthMessage={`${messages['subUser_maxLengthMessageEmail']} 100`}
                          required={true}
                          requiredMessage={messages['required_field']}
                        />
                      </Grid>
                    </Grid>
                  </>
                }
                {formData.id &&
                  <>
                    <Grid item sm={12}>
                      <Input
                        id="userNameReadOnly"
                        type="text"
                        label={messages['subUser_userName']}
                        value={formData?.userName || ""}
                        onChange={() => {}}
                        disabled
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <Input
                        id="emailReadOnly"
                        type="text"
                        label={messages['email']}
                        value={formData?.email || messages['none']}
                        onChange={() => {}}
                        disabled
                      />
                    </Grid>
                  </>
                }
                <Grid item sm={12}>
                  <Input
                    id="cellphone"
                    label={messages['subUser_phone']}
                    placeholder={messages['subUser_phone']}
                    value={formData?.phone}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        phone: value
                      })
                    }}
                    regex={/^\d*$/}
                    maxLength={15}
                    maxLengthMessage={`${messages["subUser_maxLengthMessageCellPhone"]} 15`}
                    minLength={10}
                    minLengthMessage={`${messages["subUser_minLengthMessageCellPhone"]} 10`}
                  />
                </Grid>
                <Grid item sm={12}>
                  <div style={{ display: 'flex' }}>
                    <Select
                      id="type"
                      label={messages['subUser_role']}
                      placeholder={messages['subUser_select']}
                      valueField="id"
                      textField="name"
                      options={
                        [
                          {
                            id: formData?.customRoleId ? formData.customRole?.id : 0,
                            modules: formData?.customRoleId ? formData.customRole?.modules : [],
                            name: messages['permissions_custom']
                          },
                          ...filterRoles
                        ]
                      }
                      value={
                        formData?.role ?
                          formData.role : formData?.customRole ?
                            {
                              ...formData?.customRole,
                              name: messages['permissions_custom']
                            } : null
                      }
                      onChange={(data) => {
                        if (data === null) {
                          setFormData({
                            ...formData,
                            role: null,
                            customRole: null
                          })
                        } else {
                          let valuesRole = (!data?.id || data?.id == formData.customRole?.id) ?
                            {
                              role: null,
                              customRole: {
                                ...data,
                                isCustom: true
                              }
                            } :
                            {
                              role: data,
                              customRole: null
                            }
                          setFormData({
                            ...formData,
                            ...valuesRole
                          });
                          valuesRole?.customRole?.id === 0 && turnSlidersOffOn();
                        }
                      }}
                      loading={loadingSubUserRoles || loadingSubUser || (onShow && !!subUserInfo && loadingSubUser && !selectedSubUserContext?.id) || (user?.isSubUser && loadingCurrentSubUser)}
                      disabled={loadingSubUserRoles || loadingSubUser || (onShow && !!subUserInfo && loadingSubUser && !selectedSubUserContext?.id) || (user?.isSubUser && loadingCurrentSubUser)}
                      isClearable
                      required
                      requiredMessage={messages['required_field']}
                    />
                    {(!loadingSubUserRoles && !formData?.role && formData?.customRole) &&
                      <Tooltip
                        title={messages['subUser_viewPermissions']}
                        placement={'top-start'}
                      >
                        <IconButton
                          aria-haspopup="true"
                          color="inherit"
                          style={{
                            marginTop: '28px',
                            marginLeft: '8px',
                            padding: '9px'
                          }}
                          onClick={() => turnSlidersOffOn()}
                          size="large">
                          <Icon>{"remove_red_eye"}</Icon>
                        </IconButton>
                      </Tooltip>
                    }
                  </div>
                </Grid>
                <Grid item sm={12}>
                  <TagsManage
                    onlyTags={true}
                    label={messages["settingsDriver_tags"]}
                    required={true}
                    enableSelectAll={true}
                    requiredAsteriskPosition="end"
                    mode="selector"
                    value={formData?.tags || []}
                    onChange={(values) => {
                      setFormData({ ...formData, tags: values });
                      setErrorMessage("");
                    }}
                    menuStyle={{
                      maxHeight: 350,
                      height: 350,
                      maxWidth: 430,
                      width: 430
                    }}
                    height={formData?.tags?.length ? 140 : 55}
                    isVisiblePill={true}
                  />
                  {
                    errorMessage &&
                    <small className="select-error text-danger">{errorMessage}</small>
                  }
                </Grid>
                <Grid item sm={12}>
                  <InputDate
                    id="date"
                    type="date"
                    label={messages['subUser_expiryDate']}
                    placeholder={messages['subUser_selectDate']}
                    value={formData?.expiration || ""}
                    format={GetFormatForDate(user?.dateFormat)}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        expiration: value
                      })
                    }
                    min={new Date()}
                  />
                </Grid>
                {apolloEldPermission &&
                  <Grid item sm={12} className="account-eld-toogle">
                    <ToggleSwitch
                      label={messages['subUser_apolloEldAccount']}
                      id="subuser-apollo-eld"
                      checked={eldCredentials}
                      optionLabels={["", ""]}
                      onChange={(value) => {
                        if (!value) {
                          let eldUser = {
                            password: '',
                            username: '',
                            confirmPassword: '',
                          }
                          setFormData({
                            ...formData,
                            eldUser,
                          })
                        }
                        setEldCredentials(value)
                      }}
                      small
                    />
                  </Grid>
                }


                {
                  eldCredentials && apolloEldPermission &&
                  <Grid
                    container
                    item
                    spacing={1}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid container item direction="row" spacing={1} xs={12} sm={12}>
                      <Grid item sm={12}>
                        <Input
                          id="username"
                          type="text"
                          label={messages['subUser_userName']}
                          placeholder={messages['subUser_userName']}
                          value={formData?.eldUser?.username || ""}
                          onChange={(value) => {
                            let eldUser = {
                              ...formData?.eldUser,
                              username: value
                            }
                            setFormData({
                              ...formData,
                              eldUser,
                            })
                          }}
                          minLength={2}
                          minLengthMessage={`${messages['subUser_minMessageUsername']} 2`}
                          maxLength={200}
                          maxLengthMessage={`${messages['subUser_maxMessageUsername']} 200`}
                          required={eldCredentials || (eldCredentials && !formData?.eldUser?.username) || false}
                          requiredMessage={messages['required_field']}
                        />
                      </Grid>

                      <Grid container item direction="row" spacing={1} xs={12} sm={12}>
                        <Grid item xs={6} sm={6}>
                          <Input
                            id="password"
                            type="password"
                            label={formData?.eldUser?.password ? messages['subUser_changePassword'] : messages['subUser_password']}
                            placeholder={formData?.eldUser?.password ? messages['subUser_changePassword'] : messages['subUser_password']}
                            value={formData?.eldUser?.password || ""}
                            autoComplete="new-password"
                            onChange={(value) => {
                              let eldUser = {
                                ...formData?.eldUser,
                                password: value
                              }
                              setFormData({
                                ...formData,
                                eldUser,
                              })
                            }}
                            minLength={6}
                            minLengthMessage={`${messages['subUser_minMessagePassword']} 6`}
                            maxLength={20}
                            maxLengthMessage={`${messages['subUser_maxMessagePassword']} 20`}
                            required={eldCredentials && (formData?.eldUser?.confirmPassword || !subUserSelected || (subUserSelected?.eldUser?.username && !subUserSelected?.eldUser?.password) || (!subUserSelected?.eldUser?.username && !subUserSelected?.eldUser?.password)) ? true : false}
                            requiredMessage={messages['required_field']}
                          />
                        </Grid>

                        <Grid item xs={6} sm={6}>
                          <Input
                            id="confirmPassword"
                            type="password"
                            label={messages['subUser_confirmPassword']}
                            placeholder={messages['subUser_confirmPassword']}
                            value={formData?.eldUser?.confirmPassword || ""}
                            autoComplete="new-password"
                            valueToCompare={formData?.eldUser?.password}
                            valueToCompareMessage={messages['subUser_valueToCompareMessage']}
                            onChange={(value) => {
                              let eldUser = {
                                ...formData?.eldUser,
                                confirmPassword: value
                              }
                              setFormData({
                                ...formData,
                                eldUser,
                              })
                            }
                            }
                            minLength={6}
                            minLengthMessage={`${messages['subUser_minMessagePassword']} 6`}
                            maxLength={20}
                            maxLengthMessage={`${messages['subUser_maxMessagePassword']} 20`}
                            required={eldCredentials && (formData?.eldUser?.confirmPassword || !subUserSelected || (subUserSelected?.eldUser?.username && !subUserSelected?.eldUser?.password) || (!subUserSelected?.eldUser?.username && !subUserSelected?.eldUser?.password)) ? true : false}
                            requiredMessage={messages['required_field']}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                }

              </Grid>
            </Form>
          </div>
        </ModalBody>

        <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }}>
          <Button
            onClick={handleClose}
            className="btn-cancel"
          >
            {messages["cancel"]}
          </Button>
          <Button
            className="btn-blue"
            onClick={(e) => {
              e.preventDefault();
              formRef.current.submit();
            }}
          >
            {messages[`save`]}
          </Button>
        </ModalFooter>
      </Modal>
      {/* Reset Subuser */}
      <Modal
        size={'small'}
        open={onShowModalResetUser}
        handleClose={() => setOnShowModalResetUser(!onShowModalResetUser)}
        title={`${messages["subUser_resetUser"]}`}
      >
        <ModalBody>
          <div style={{ padding: '0px 25px' }}>
            <p style={{ overflowWrap: 'anywhere' }}>
              {`${messages['subUser_resetPermissionMessage']}`}
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            id={'btn-cancel-vehicle'}
            type="button"
            onClick={() => setOnShowModalResetUser(!onShowModalResetUser)}
            className="btn-cancel"
          >
            {messages['cancel']}
          </Button>

          <Button
            id={'btn-schedule'}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setOnShowModalResetUser(!onShowModalResetUser)
              resetUserCognito(formData.id, formData.email)
            }}
            className="btn-red"
          >
            {messages['subUser_resetCognito']}
          </Button>
        </ModalFooter>
      </Modal>
      {/* New Role Custom SubUser */}
      {
        onShowPermissions &&
        <CreditEditRol
          onShow={onShowPermissions}
          onClose={(role) => {
            let valuesRole = (!role?.id || role?.isCustom || role?.id == formData?.customRole?.id) ?
              {
                role: null,
                customRole: role
              } :
              {
                role: role,
                customRole: null
              }
            turnSlidersOffOn();
            setFormData({
              ...formData,
              ...valuesRole
            });
          }}
          hideSupervisor={user?.isSubUser && user?.subUserId === selectedSubUserContext?.id ? true : false}
          changeRoleCustom={formData?.customRole}
        />
      }
    </Fragment >
  );
}

export default CreditEditSubUser;